import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Upload, X, FileText, CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, Info } from "lucide-react";

// ─── All world countries in Mongolian ────────────────────────────────────────
const COUNTRIES = [
  "Афганистан","Албани","Алжир","Андорра","Ангол","Антигуа ба Барбуда","Аргентин","Армени",
  "Австрали","Австри","Азербайжан","Багамын арлууд","Бахрейн","Бангладеш","Барбадос","Беларусь",
  "Бельги","Белиз","Бенин","Бутан","Боливи","Босни Герцеговин","Ботсвана","Бразил","Бруней",
  "Болгар","Буркина Фасо","Бурунди","Кабо Верде","Камбож","Камерун","Канад","Төв Африкийн Бүгд Найрамдах Улс",
  "Чад","Чили","Хятад","Колумби","Коморын арлууд","Конго","Коста Рика","Кот д'Ивуар","Хорват",
  "Куба","Кипр","Чех","Данмарк","Жибути","Доминик","Доминикан Бүгд Найрамдах Улс","Эквадор",
  "Египет","Эль Сальвадор","Экваторын Гвиней","Эритрей","Эстони","Эсватини","Этиоп","Фижи",
  "Финланд","Франц","Габон","Гамби","Гүрж","Герман","Гана","Грек","Гренад","Гватемал",
  "Гвиней","Гвиней-Бисау","Гайан","Гаити","Гондурас","Унгар","Исланд","Энэтхэг","Индонез",
  "Иран","Ирак","Ирланд","Израиль","Итали","Ямайк","Япон","Иордан","Казахстан","Кени",
  "Кирибати","Хойд Солонгос","Өмнөд Солонгос","Косово","Кувейт","Киргизстан","Лаос","Латви",
  "Ливан","Лесото","Либери","Ливи","Лихтенштейн","Литва","Люксембург","Мадагаскар","Малави",
  "Малайз","Мальдив","Мали","Мальта","Маршаллын арлууд","Мавритани","Маврикий","Мексик",
  "Микронези","Молдов","Монако","Монгол","Монтенегро","Марокко","Мозамбик","Мьянмар","Намиби",
  "Науру","Непал","Нидерланд","Шинэ Зеланд","Никарагуа","Нигер","Нигери","Хойд Македони",
  "Норвеги","Оман","Пакистан","Палау","Панам","Папуа Шинэ Гвиней","Парагвай","Перу",
  "Филиппин","Польш","Португал","Катар","Румын","Орос","Руанда","Сент Китс ба Невис",
  "Сент Люси","Сент Винсент ба Гренадин","Самоа","Сан Марино","Сао Томе ба Принсипи",
  "Саудын Араб","Сенегал","Серби","Сейшелийн арлууд","Сьерра Леоне","Сингапур","Словак",
  "Словени","Соломоны арлууд","Сомали","Өмнөд Африк","Өмнөд Судан","Испани","Шри Ланка",
  "Судан","Суринам","Швед","Швейцарь","Сири","Тайван","Тажикистан","Танзани","Тайланд",
  "Тимор-Лесте","Того","Тонга","Тринидад ба Тобаго","Тунис","Турк","Туркменистан","Тувалу",
  "Уганда","Украин","Нэгдсэн Арабын Эмират","Их Британи","АНУ","Уругвай","Узбекистан",
  "Вануату","Ватикан","Венесуэл","Вьетнам","Йемен","Замби","Зимбабве","Бусад"
];

// ─── Document categories ──────────────────────────────────────────────────────
const DOC_CATEGORIES = [
  { key: "passport", label: "Гадаад паспорт (одоо ашиглаж байгаа + хугацаа дууссан бүгд)" },
  { key: "irgenii_unmehleh", label: "Иргэний үнэмлэх" },
  { key: "gerleltiin_gerchilgee", label: "Гэрлэлтийн гэрчилгээ (e-mongolia эсвэл эх хувь)" },
  { key: "ajliin_todorkhiilolt", label: "Ажлын газрын тодорхойлолт (албан тушаал, ажилд орсон огноо, цалингийн хэмжээ)" },
  { key: "business_docs", label: "Бизнес эрхэлдэг бол: Компанийн гэрчилгээ, дүрэм, татвар, УХХ" },
  { key: "nd_khulga", label: "НД хуулга 6 сар (e-mongolia англи) + тэтгэвэрийн лавлагаа (хэрэв байгаа бол)" },
  { key: "dans_khulga", label: "Дансны дэлгэрэнгүй хуулга сүүлийн 4 сар (англи, $ үлдэгдэлтэй)" },
  { key: "khadgalamj", label: "Хадгаламж байгаа бол: дэлгэрэнгүй хуулга + тодорхойлолт (англи)" },
  { key: "ul_khodlokh", label: "Үл хөдлөх лавлагаа (e-mongolia)" },
  { key: "busad_sankhuu", label: "Бусад санхүү дэмжих материалууд (түрээсийн гэрээ, ажил гүйцэтгэх гэрээ г.м.)" },
  { key: "photo", label: "Цээж зураг файлаар (3.5x4.5 см, цагаан дэвсгэртэй)" },
];

// ─── Steps ────────────────────────────────────────────────────────────────────
const STEPS = [
  "Бэлтгэх зүйлс",          // 0 - Preparation warning
  "Ерөнхий мэдээлэл",        // 1
  "Өмнөх Канадын өргөдөл",   // 2
  "UCI дугаар",               // 3
  "Хувийн мэдээлэл",         // 4
  "Паспортын мэдээлэл",      // 5
  "Холбоо барих / Хаяг",     // 6
  "Одоогийн статус",          // 7
  "Гэр бүлийн байдал",       // 8
  "Эцэг эхийн мэдээлэл",     // 9
  "Ах эгч дүүнүүд",          // 10
  "Хүүхдүүд",                // 11
  "Боловсрол",                // 12
  "Сүүлийн 10 жилийн үйл ажиллагаа", // 13
  "Канад руу явах төлөвлөгөө", // 14
  "Урьж байгаа хүн",         // 15
  "Аяллын түүх",             // 16
  "Визний татгалзал",        // 17
  "Эрүүл мэнд",              // 18
  "Гэмт хэрэг / Цэрэг",     // 19
  "Санхүүгийн мэдээлэл",    // 20
  "Эх орондоо буцах холбоо", // 21
  "Баримт бичиг оруулах",   // 22
  "GCKey бэлтгэл",           // 23
  "Эцсийн баталгаажуулалт", // 24
];

type FormValues = Record<string, string | boolean | unknown[]>;
interface DocFile { id?: number; fileName: string; fileUrl?: string; uploading?: boolean; }
type DocMap = Record<string, DocFile[]>;

// ─── Sibling entry type ───────────────────────────────────────────────────────
interface SiblingEntry {
  lastName: string; firstName: string; dob: string; country: string;
  relationship: string; alive: string; occupation: string;
}

// ─── Child entry type ─────────────────────────────────────────────────────────
interface ChildEntry {
  lastName: string; firstName: string; dob: string; country: string;
  relationship: string; alive: string; maritalStatus: string;
}

// ─── Activity entry type ──────────────────────────────────────────────────────
interface ActivityEntry {
  fromDate: string; toDate: string; type: string; employer: string;
  occupation: string; city: string; country: string;
}

// ─── Travel history entry ─────────────────────────────────────────────────────
interface TravelEntry {
  country: string; fromDate: string; toDate: string; purpose: string;
}

export default function CanadaVisaFormPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";

  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [docs, setDocs] = useState<DocMap>({});
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Siblings
  const [siblings, setSiblings] = useState<SiblingEntry[]>([]);
  // Children
  const [children, setChildren] = useState<ChildEntry[]>([]);
  // Activities
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  // Travel history
  const [travels, setTravels] = useState<TravelEntry[]>([]);

  // Agreements
  const [agreements, setAgreements] = useState({ a1: false, a2: false, a3: false, a4: false, a5: false });
  const [signatureName, setSignatureName] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsRead, setTermsRead] = useState(false);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: appData, isLoading, error } = trpc.canadaVisa.getByToken.useQuery(
    { token }, { enabled: !!token, retry: false }
  );

  const saveProgressMutation = trpc.canadaVisa.saveProgress.useMutation();
  const uploadDocMutation = trpc.canadaVisa.uploadDocument.useMutation();
  const deleteDocMutation = trpc.canadaVisa.deleteDocument.useMutation();
  const submitMutation = trpc.canadaVisa.submit.useMutation();

  useEffect(() => {
    if (appData) {
      if (appData.status === "submitted") { setSubmitted(true); return; }
      try {
        const saved = JSON.parse(appData.formData ?? "{}") as FormValues;
        setFormValues(saved);
        if (saved._siblings) setSiblings(saved._siblings as SiblingEntry[]);
        if (saved._children) setChildren(saved._children as ChildEntry[]);
        if (saved._activities) setActivities(saved._activities as ActivityEntry[]);
        if (saved._travels) setTravels(saved._travels as TravelEntry[]);
      } catch {}
      const docMap: DocMap = {};
      for (const doc of appData.documents ?? []) {
        if (!docMap[doc.category]) docMap[doc.category] = [];
        docMap[doc.category].push({ id: doc.id, fileName: doc.fileName, fileUrl: doc.fileUrl });
      }
      setDocs(docMap);
    }
  }, [appData]);

  useEffect(() => { if (error) setNotFound(true); }, [error]);

  const buildFullData = useCallback((base: FormValues) => ({
    ...base, _siblings: siblings, _children: children, _activities: activities, _travels: travels,
  }), [siblings, children, activities, travels]);

  const setField = (key: string, value: string | boolean | unknown[]) => {
    const updated = { ...formValues, [key]: value };
    setFormValues(updated);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      triggerAutoSave(updated);
    }, 2000);
  };

  const triggerAutoSave = useCallback(async (data: FormValues) => {
    if (!token || submitted) return;
    setSaving(true);
    try {
      await saveProgressMutation.mutateAsync({
        token,
        formData: JSON.stringify(buildFullData(data)),
        currentStep,
        clientEmail: (data.email as string) || undefined,
        clientName: (data.fullNameMongolian as string) || undefined,
        clientPhone: (data.phone as string) || undefined,
      });
    } catch {}
    setSaving(false);
  }, [token, currentStep, submitted, saveProgressMutation, buildFullData]);

  const handleNext = async () => {
    setSaving(true);
    try {
      await saveProgressMutation.mutateAsync({
        token,
        formData: JSON.stringify(buildFullData(formValues)),
        currentStep: currentStep + 1,
        clientEmail: (formValues.email as string) || undefined,
        clientName: (formValues.fullNameMongolian as string) || undefined,
        clientPhone: (formValues.phone as string) || undefined,
      });
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    } catch { toast.error("Хадгалахад алдаа гарлаа"); }
    setSaving(false);
  };

  const handlePrev = () => { setCurrentStep(s => Math.max(0, s - 1)); window.scrollTo(0, 0); };

  const handleFileUpload = async (category: string, file: File) => {
    if (file.size > 15 * 1024 * 1024) { toast.error("Файлын хэмжээ 15MB-аас хэтэрч байна"); return; }
    const placeholder: DocFile = { fileName: file.name, uploading: true };
    setDocs(prev => ({ ...prev, [category]: [...(prev[category] ?? []), placeholder] }));
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(",")[1] ?? "";
      try {
        const result = await uploadDocMutation.mutateAsync({ token, category, fileName: file.name, fileData: base64, mimeType: file.type, fileSize: file.size });
        setDocs(prev => {
          const list = (prev[category] ?? []).filter(d => d !== placeholder);
          return { ...prev, [category]: [...list, { id: result.id, fileName: result.fileName, fileUrl: result.url }] };
        });
        toast.success("Файл амжилттай оруулагдлаа");
      } catch {
        setDocs(prev => ({ ...prev, [category]: (prev[category] ?? []).filter(d => d !== placeholder) }));
        toast.error("Файл оруулахад алдаа гарлаа");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteDoc = async (category: string, doc: DocFile) => {
    if (!doc.id) return;
    try {
      await deleteDocMutation.mutateAsync({ token, documentId: doc.id });
      setDocs(prev => ({ ...prev, [category]: (prev[category] ?? []).filter(d => d.id !== doc.id) }));
    } catch { toast.error("Устгахад алдаа гарлаа"); }
  };

  const handleSubmit = async () => {
    if (!Object.values(agreements).every(Boolean)) { toast.error("Бүх зөвшөөрлийг сонгоно уу"); return; }
    if (!signatureName.trim()) { toast.error("Нэрээ бичнэ үү"); return; }
    try {
      await submitMutation.mutateAsync({
        token,
        formData: JSON.stringify({ ...buildFullData(formValues), signatureName, submittedAt: new Date().toISOString() }),
        clientEmail: (formValues.email as string) || undefined,
        clientName: (formValues.fullNameMongolian as string) || undefined,
        clientPhone: (formValues.phone as string) || undefined,
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Алдаа гарлаа");
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const F = (key: string) => formValues[key] as string ?? "";
  const setF = (key: string) => (v: string) => setField(key, v);

  const field = (label: string, key: string, placeholder = "", type = "text") => (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Input type={type} value={F(key)} onChange={e => setF(key)(e.target.value)} placeholder={placeholder} className="bg-white" />
    </div>
  );

  const dateField = (label: string, key: string) => (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Input type="date" value={F(key)} onChange={e => setF(key)(e.target.value)} className="bg-white" />
    </div>
  );

  const textareaField = (label: string, key: string, rows = 3) => (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Textarea value={F(key)} onChange={e => setF(key)(e.target.value)} rows={rows} className="bg-white" />
    </div>
  );

  const selectField = (label: string, key: string, options: string[]) => (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Select value={F(key)} onValueChange={setF(key)}>
        <SelectTrigger className="bg-white"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
        <SelectContent className="max-h-60">
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const countryField = (label: string, key: string) => selectField(label, key, COUNTRIES);

  const yesNo = (label: string, key: string) => (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Select value={F(key)} onValueChange={setF(key)}>
        <SelectTrigger className="bg-white"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="Тийм">Тийм</SelectItem>
          <SelectItem value="Үгүй">Үгүй</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const sectionTitle = (title: string) => (
    <h3 className="font-semibold text-base text-blue-900 border-b border-blue-100 pb-2 mb-3">{title}</h3>
  );

  const infoBox = (text: string) => (
    <div className="flex gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
      <Info className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );

  // ─── Step content ─────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {

      // ── Step 0: Preparation warning ──────────────────────────────────────
      case 0: return (
        <div className="space-y-5">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-amber-800 text-lg">Маягт бөглөхөөс өмнө уншина уу!</h3>
            </div>
            <p className="text-amber-800 text-sm mb-4">
              Энэхүү анкетийг бөглөхдөө дараах мэдээллүүдийг гартаа бэлэн байлгана уу:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-amber-900">
              {[
                "📘 Гадаад паспорт (одоо ашиглаж байгаа болон хугацаа дууссан бүгд)",
                "🪪 Иргэний үнэмлэх",
                "📅 Аав, ээжийн овог, нэр, төрсөн огноо, төрсөн улс",
                "👨‍👩‍👧‍👦 Ах, эгч, дүүнүүдийн овог, нэр, төрсөн огноо, одоогийн амьдарч байгаа улс",
                "💍 Эхнэр/нөхрийн хувийн мэдээлэл (хэрэв гэрлэсэн бол)",
                "🏠 Одоогийн оршин суугаа хаяг (дэлгэрэнгүй)",
                "🏢 Ажлын газрын нэр, хаяг, утас, ажилд орсон огноо",
                "🎓 Боловсролын мэдээлэл (сургуулийн нэр, хаяг, он жил)",
                "✈️ Сүүлийн 10 жилийн гадаад аяллын мэдээлэл",
                "💰 Банкны дансны мэдээлэл (үлдэгдэл, хуулга)",
                "📋 Сүүлийн 10 жилийн ажил, сургуулийн мэдээлэл",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 bg-white rounded p-2 border border-amber-200">
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-green-700" />
              <h4 className="font-semibold text-green-800">Чухал мэдэгдэл</h4>
            </div>
            <ul className="text-sm text-green-800 space-y-1.5">
              <li>✅ <strong>Мэдэхгүй зүйлийг алгасаж болно</strong> — бүх асуултанд хариулах шаардлагагүй. Мэдэхгүй эсвэл хамааралгүй зүйлийг хоосон орхиж болно.</li>
              <li>✅ Маягтыг хэдэн удаа хадгалж, дараа нь үргэлжлүүлж болно.</li>
              <li>✅ Файлуудыг PDF, JPG, PNG хэлбэрээр оруулна (15MB хүртэл).</li>
            </ul>
          </div>


        </div>
      );

      // ── Step 1: General info ──────────────────────────────────────────────
      case 1: return (
        <div className="space-y-4">
          {sectionTitle("Ерөнхий мэдээлэл")}
          {selectField("Та Канад руу ямар зорилгоор явах вэ?", "purpose", [
            "Аялахаар","Гэр бүл, хамаатан садандаа зочлохоор","Найзындаа зочлохоор",
            "Бизнесийн уулзалтаар","Хурал, арга хэмжээнд оролцохоор",
            "Богино хугацааны сургалтад хамрагдахаар","Дамжин өнгөрөхөөр","Бусад"
          ])}
          {F("purpose") === "Бусад" && textareaField("Зорилгоо тайлбарлана уу", "purposeOther")}
          {selectField("Та ганцаараа мэдүүлэх үү?", "travelGroup", [
            "Ганцаараа","Эхнэр эсвэл нөхөртэйгөө","Хүүхдүүдтэйгээ","Гэр бүлээрээ","Өөр хүнтэй хамт"
          ])}
          {F("travelGroup") !== "Ганцаараа" && F("travelGroup") !== "" && field("Хамт мэдүүлэх хүний тоо", "groupCount")}
          {countryField("Та аль улсаас өргөдлөө гаргах вэ?", "applyingFrom")}
          {countryField("Та одоогоор аль улсад амьдарч байна вэ?", "currentCountry")}
          {field("Канадын виз үйлчилгээг хэний нэр дээр авч байна вэ?", "serviceFor")}
          {field("Холбогдох имэйл хаяг", "email", "example@gmail.com", "email")}
          {field("Холбогдох утасны дугаар", "phone", "+976 ...")}
        </div>
      );

      // ── Step 2: Previous Canada applications ─────────────────────────────
      case 2: return (
        <div className="space-y-4">
          {sectionTitle("Өмнөх Канадын өргөдөл")}
          {selectField("Та өмнө нь Канадын виз мэдүүлж байсан уу?", "prevCanadaApp", ["Тийм","Үгүй","Мэдэхгүй"])}
          {F("prevCanadaApp") === "Тийм" && <>
            {selectField("Ямар төрлийн өргөдөл гаргасан бэ?", "prevAppType", [
              "Аяллын виз","Сурах зөвшөөрөл","Ажиллах зөвшөөрөл",
              "Эцэг эх, өвөө эмээгийн урт хугацааны виз","Байнгын оршин суух өргөдөл",
              "Дамжин өнгөрөх виз","Бусад"
            ])}
            {field("Хэдэн онд өргөдөл гаргасан бэ?", "prevAppYear")}
            {selectField("Өргөдлийн шийдвэр ямар гарсан бэ?", "prevAppDecision", [
              "Зөвшөөрөгдсөн","Татгалзсан","Буцаагдсан","Өөрөө цуцалсан","Одоо хүлээгдэж байгаа"
            ])}
            {field("Өргөдлийн дугаар (байгаа бол)", "prevAppNumber")}
            {yesNo("Биометр (хурууны хээ, нүдний шилбэ) өгсөн үү?", "prevBiometric")}
            {F("prevBiometric") === "Тийм" && <>
              {field("Биометр өгсөн он", "biometricYear")}
              {countryField("Биометр өгсөн улс", "biometricCountry")}
            </>}
          </>}
          {yesNo("Та Канадад өмнө нь байсан уу?", "prevInCanada")}
          {F("prevInCanada") === "Тийм" && <>
            {field("Хамгийн сүүлд Канадад байсан он", "lastInCanadaYear")}
            {field("Хэр удаан байсан бэ?", "lastInCanadaDuration")}
            {selectField("Ямар статустай байсан бэ?", "lastInCanadaStatus", [
              "Аяллын виз","Сурах зөвшөөрөл","Ажиллах зөвшөөрөл","Байнгын оршин суугч","Иргэн","Бусад"
            ])}
          </>}
        </div>
      );

      // ── Step 3: UCI ───────────────────────────────────────────────────────
      case 3: return (
        <div className="space-y-4">
          {sectionTitle("UCI дугаар (Unique Client Identifier)")}
          {infoBox("UCI дугаар нь Канадын иммиграцийн системд таны хувийн дугаар юм. Өмнө нь Канадын виз мэдүүлж байсан бол паспорт дээрх визний хуудас эсвэл өмнөх шийдвэрийн захидлаас олдоно.")}
          {yesNo("Та UCI дугаартай юу?", "hasUCI")}
          {F("hasUCI") === "Тийм" && field("UCI дугаар", "uciNumber", "Жишээ: 0001-2345-6789")}
        </div>
      );

      // ── Step 4: Personal info (IMM 5257 Section A) ────────────────────────
      case 4: return (
        <div className="space-y-4">
          {sectionTitle("Хувийн мэдээлэл")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Монгол нэр (овог нэр)", "fullNameMongolian", "Жишээ: Батын Болд")}
            {field("Овог (латин үсгээр)", "lastNameLatin", "Жишээ: BAT")}
            {field("Нэр (латин үсгээр)", "firstNameLatin", "Жишээ: BOLD")}
            {field("Дунд нэр (байгаа бол)", "middleName")}
          </div>
          {yesNo("Та өөр нэрээр мэдэгддэг үү? (хуучин нэр, хочит нэр гэх мэт)", "hasAlias")}
          {F("hasAlias") === "Тийм" && field("Өөр нэр", "aliasName")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dateField("Төрсөн огноо", "dob")}
            {countryField("Төрсөн улс", "birthCountry")}
            {field("Төрсөн хот/аймаг", "birthCity")}
            {selectField("Хүйс", "gender", ["Эрэгтэй","Эмэгтэй","Бусад / Тодорхойгүй"])}
          </div>
          {countryField("Иргэншил (паспортын улс)", "nationality")}
          {yesNo("Та өөр улсын иргэншилтэй юу?", "hasOtherNationality")}
          {F("hasOtherNationality") === "Тийм" && countryField("Өөр иргэншлийн улс", "otherNationality")}
          {selectField("Шашин шүтлэг (заавал биш)", "religion", [
            "Буддын шашин","Христийн шашин","Ислам","Бусад","Шашингүй","Хэлэхийг хүсэхгүй байна"
          ])}
          {selectField("Угсаатны бүлэг (заавал биш)", "ethnicity", [
            "Монгол","Казак","Буриад","Тува","Хятад","Бусад","Хэлэхийг хүсэхгүй байна"
          ])}
        </div>
      );

      // ── Step 5: Passport info ─────────────────────────────────────────────
      case 5: return (
        <div className="space-y-4">
          {sectionTitle("Паспортын мэдээлэл")}
          {selectField("Паспортын төрөл", "passportType", [
            "Энгийн паспорт","Дипломат паспорт","Албан паспорт","Онцгой паспорт","Дүрвэгчийн паспорт","Бусад"
          ])}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Паспортын дугаар", "passportNumber")}
            {countryField("Паспорт олгосон улс", "passportCountry")}
            {field("Паспорт олгосон байгууллага / хот", "passportIssuedBy")}
            {dateField("Паспорт олгосон огноо", "passportIssueDate")}
            {dateField("Паспортын хугацаа дуусах огноо", "passportExpiry")}
          </div>
          {yesNo("Та хугацаа дууссан паспорттай юу?", "hasExpiredPassport")}
          {F("hasExpiredPassport") === "Тийм" && <>
            {field("Хугацаа дууссан паспортын дугаар", "expiredPassportNumber")}
            {dateField("Хугацаа дуусах огноо", "expiredPassportExpiry")}
          </>}
          {sectionTitle("Иргэний үнэмлэхний мэдээлэл")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Иргэний үнэмлэхний дугаар", "nationalIdNumber")}
            {countryField("Иргэний үнэмлэх олгосон улс", "nationalIdCountry")}
            {dateField("Иргэний үнэмлэх олгосон огноо", "nationalIdIssueDate")}
            {dateField("Иргэний үнэмлэхний хугацаа дуусах огноо", "nationalIdExpiry")}
          </div>
        </div>
      );

      // ── Step 6: Contact / Address ─────────────────────────────────────────
      case 6: return (
        <div className="space-y-4">
          {sectionTitle("Одоогийн оршин суугаа хаяг")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {countryField("Оршин суугаа улс", "residenceCountry")}
            {field("Муж / Аймаг / Бүс", "residenceProvince")}
            {field("Хот / Сум", "residenceCity")}
            {field("Дүүрэг / Хороо", "residenceDistrict")}
            {field("Гудамж / Байр / Тоот", "residenceStreet")}
            {field("Шуудангийн код (байгаа бол)", "residenceZip")}
          </div>
          {yesNo("Таны оршин суугаа хаяг паспортын хаягтай ижил үү?", "sameAsPassportAddress")}
          {F("sameAsPassportAddress") === "Үгүй" && <>
            {sectionTitle("Паспортын хаяг")}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {countryField("Паспортын хаягийн улс", "passportAddressCountry")}
              {field("Паспортын хаяг (дэлгэрэнгүй)", "passportAddress")}
            </div>
          </>}
          {sectionTitle("Холбоо барих мэдээлэл")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Утасны дугаар (гэрийн)", "phoneHome")}
            {field("Утасны дугаар (гар утас)", "phoneMobile")}
            {field("Ажлын утас", "phoneWork")}
            {field("Имэйл хаяг", "emailContact", "example@gmail.com", "email")}
          </div>
        </div>
      );

      // ── Step 7: Current status ────────────────────────────────────────────
      case 7: return (
        <div className="space-y-4">
          {sectionTitle("Одоогийн статус")}
          {selectField("Та одоо аль улсад хууль ёсоор оршин суугаа вэ?", "currentLegalStatus", [
            "Монголын иргэн","Байнгын оршин суугч","Цагаачлалын визтэй","Ажиллах зөвшөөрөлтэй",
            "Сурах зөвшөөрөлтэй","Дүрвэгч","Бусад"
          ])}
          {F("currentLegalStatus") !== "Монголын иргэн" && F("currentLegalStatus") !== "" && <>
            {field("Оршин суух зөвшөөрлийн дугаар", "residencePermitNumber")}
            {dateField("Оршин суух зөвшөөрлийн хугацаа дуусах огноо", "residencePermitExpiry")}
          </>}
          {yesNo("Та одоо аль нэг улсаас виз эсвэл оршин суух зөвшөөрөл хүлээж байгаа уу?", "pendingVisa")}
          {F("pendingVisa") === "Тийм" && <>
            {countryField("Ямар улсаас хүлээж байгаа вэ?", "pendingVisaCountry")}
            {field("Ямар төрлийн виз/зөвшөөрөл вэ?", "pendingVisaType")}
          </>}
        </div>
      );

      // ── Step 8: Marital status ────────────────────────────────────────────
      case 8: return (
        <div className="space-y-4">
          {sectionTitle("Гэр бүлийн байдал")}
          {selectField("Гэр бүлийн байдал", "maritalStatus", [
            "Гэрлээгүй","Гэрлэсэн","Хуулийн дагуу хамтран амьдарч байгаа",
            "Гэрлэлтээ цуцлуулсан","Бэлэвсэн","Тусдаа амьдарч байгаа"
          ])}
          {(F("maritalStatus") === "Гэрлэсэн" || F("maritalStatus") === "Хуулийн дагуу хамтран амьдарч байгаа") && <>
            {sectionTitle("Эхнэр / Нөхрийн мэдээлэл")}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Эхнэр/нөхрийн овог (латин)", "spouseLastName")}
              {field("Эхнэр/нөхрийн нэр (латин)", "spouseFirstName")}
              {field("Эхнэр/нөхрийн Монгол нэр", "spouseNameMongolian")}
              {dateField("Эхнэр/нөхрийн төрсөн огноо", "spouseDob")}
              {countryField("Эхнэр/нөхрийн төрсөн улс", "spouseBirthCountry")}
              {field("Эхнэр/нөхрийн төрсөн хот", "spouseBirthCity")}
              {countryField("Эхнэр/нөхрийн иргэншил", "spouseNationality")}
              {dateField("Гэрлэсэн огноо", "marriageDate")}
              {countryField("Гэрлэсэн улс", "marriageCountry")}
              {field("Гэрлэсэн хот", "marriageCity")}
            </div>
            {yesNo("Эхнэр/нөхөр нь таны хамт Канад руу явах уу?", "spouseTraveling")}
          </>}
          {(F("maritalStatus") === "Гэрлэлтээ цуцлуулсан" || F("maritalStatus") === "Тусдаа амьдарч байгаа") && <>
            {sectionTitle("Өмнөх эхнэр / нөхрийн мэдээлэл")}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Өмнөх эхнэр/нөхрийн овог (латин)", "prevSpouseLastName")}
              {field("Өмнөх эхнэр/нөхрийн нэр (латин)", "prevSpouseFirstName")}
              {dateField("Өмнөх эхнэр/нөхрийн төрсөн огноо", "prevSpouseDob")}
              {dateField("Гэрлэлт цуцлагдсан огноо", "divorceDate")}
            </div>
          </>}
        </div>
      );

      // ── Step 9: Parents info (IMM 5645) ───────────────────────────────────
      case 9: return (
        <div className="space-y-5">
          {sectionTitle("Эцгийн мэдээлэл")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Эцгийн овог (латин)", "fatherLastName")}
            {field("Эцгийн нэр (латин)", "fatherFirstName")}
            {field("Эцгийн Монгол нэр", "fatherNameMongolian")}
            {dateField("Эцгийн төрсөн огноо", "fatherDob")}
            {countryField("Эцгийн төрсөн улс", "fatherBirthCountry")}
            {field("Эцгийн төрсөн хот", "fatherBirthCity")}
            {countryField("Эцгийн одоогийн оршин суугаа улс", "fatherResidenceCountry")}
            {field("Эцгийн одоогийн ажил мэргэжил", "fatherOccupation")}
          </div>
          {yesNo("Эцэг нь амьд уу?", "fatherAlive")}
          {F("fatherAlive") === "Үгүй" && dateField("Эцэг нас барсан огноо", "fatherDeathDate")}

          {sectionTitle("Эхийн мэдээлэл")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Эхийн овог (латин)", "motherLastName")}
            {field("Эхийн нэр (латин)", "motherFirstName")}
            {field("Эхийн Монгол нэр", "motherNameMongolian")}
            {dateField("Эхийн төрсөн огноо", "motherDob")}
            {countryField("Эхийн төрсөн улс", "motherBirthCountry")}
            {field("Эхийн төрсөн хот", "motherBirthCity")}
            {countryField("Эхийн одоогийн оршин суугаа улс", "motherResidenceCountry")}
            {field("Эхийн одоогийн ажил мэргэжил", "motherOccupation")}
          </div>
          {yesNo("Эх нь амьд уу?", "motherAlive")}
          {F("motherAlive") === "Үгүй" && dateField("Эх нас барсан огноо", "motherDeathDate")}
        </div>
      );

      // ── Step 10: Siblings (IMM 5645) ──────────────────────────────────────
      case 10: return (
        <div className="space-y-4">
          {sectionTitle("Ах эгч дүүнүүд")}
          {infoBox("Бүх ах, эгч, дүүгийнхээ мэдээллийг оруулна уу. Мэдэхгүй зүйлийг хоосон орхиж болно.")}
          {siblings.map((s, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm text-gray-700">{i + 1}-р ах/эгч/дүү</span>
                <Button size="sm" variant="ghost" className="text-red-500 h-7"
                  onClick={() => setSiblings(prev => prev.filter((_, idx) => idx !== i))}>
                  <X className="w-3 h-3 mr-1" /> Устгах
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ["Овог (латин)", "lastName"], ["Нэр (латин)", "firstName"],
                  ["Төрсөн огноо", "dob"], ["Төрсөн улс", "country"],
                  ["Харилцааны байдал", "relationship"], ["Амьд эсэх", "alive"],
                  ["Одоогийн ажил мэргэжил", "occupation"],
                ].map(([label, key]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-gray-600">{label}</Label>
                    {key === "country" ? (
                      <Select value={s[key as keyof SiblingEntry] || ""} onValueChange={v => setSiblings(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent className="max-h-60">{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : key === "relationship" ? (
                      <Select value={s[key as keyof SiblingEntry] || ""} onValueChange={v => setSiblings(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent>
                          {["Ах","Эгч","Дүү (эрэгтэй)","Дүү (эмэгтэй)"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : key === "alive" ? (
                      <Select value={s[key as keyof SiblingEntry] || ""} onValueChange={v => setSiblings(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Тийм">Амьд</SelectItem>
                          <SelectItem value="Үгүй">Нас барсан</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : key === "dob" ? (
                      <Input type="date" value={s[key as keyof SiblingEntry] || ""} onChange={e => setSiblings(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: e.target.value } : x))} className="bg-white h-8 text-sm" />
                    ) : (
                      <Input value={s[key as keyof SiblingEntry] || ""} onChange={e => setSiblings(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: e.target.value } : x))} className="bg-white h-8 text-sm" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full border-dashed" onClick={() => setSiblings(prev => [...prev, { lastName: "", firstName: "", dob: "", country: "", relationship: "", alive: "", occupation: "" }])}>
            + Ах/эгч/дүү нэмэх
          </Button>
          {siblings.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-2">Ах эгч дүүгүй бол хоосон орхиж болно</p>
          )}
        </div>
      );

      // ── Step 11: Children ─────────────────────────────────────────────────
      case 11: return (
        <div className="space-y-4">
          {sectionTitle("Хүүхдүүд")}
          {infoBox("18-аас дээш насны хүүхдүүдийг ч оруулна уу.")}
          {children.map((c, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm text-gray-700">{i + 1}-р хүүхэд</span>
                <Button size="sm" variant="ghost" className="text-red-500 h-7"
                  onClick={() => setChildren(prev => prev.filter((_, idx) => idx !== i))}>
                  <X className="w-3 h-3 mr-1" /> Устгах
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ["Овог (латин)", "lastName"], ["Нэр (латин)", "firstName"],
                  ["Төрсөн огноо", "dob"], ["Төрсөн улс", "country"],
                  ["Харилцааны байдал", "relationship"], ["Амьд эсэх", "alive"],
                  ["Гэр бүлийн байдал", "maritalStatus"],
                ].map(([label, key]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-gray-600">{label}</Label>
                    {key === "country" ? (
                      <Select value={c[key as keyof ChildEntry] || ""} onValueChange={v => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent className="max-h-60">{COUNTRIES.map(co => <SelectItem key={co} value={co}>{co}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : key === "relationship" ? (
                      <Select value={c[key as keyof ChildEntry] || ""} onValueChange={v => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent>
                          {["Хүүхэд (биологийн)","Үрчлэн авсан хүүхэд","Хойд хүүхэд"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : key === "alive" ? (
                      <Select value={c[key as keyof ChildEntry] || ""} onValueChange={v => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Тийм">Амьд</SelectItem>
                          <SelectItem value="Үгүй">Нас барсан</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : key === "maritalStatus" ? (
                      <Select value={c[key as keyof ChildEntry] || ""} onValueChange={v => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: v } : x))}>
                        <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                        <SelectContent>
                          {["Гэрлээгүй","Гэрлэсэн","Гэрлэлтээ цуцлуулсан","Бэлэвсэн"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : key === "dob" ? (
                      <Input type="date" value={c[key as keyof ChildEntry] || ""} onChange={e => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: e.target.value } : x))} className="bg-white h-8 text-sm" />
                    ) : (
                      <Input value={c[key as keyof ChildEntry] || ""} onChange={e => setChildren(prev => prev.map((x, idx) => idx === i ? { ...x, [key]: e.target.value } : x))} className="bg-white h-8 text-sm" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full border-dashed" onClick={() => setChildren(prev => [...prev, { lastName: "", firstName: "", dob: "", country: "", relationship: "", alive: "", maritalStatus: "" }])}>
            + Хүүхэд нэмэх
          </Button>
          {children.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Хүүхэдгүй бол хоосон орхиж болно</p>}
        </div>
      );

      // ── Step 12: Education ────────────────────────────────────────────────
      case 12: return (
        <div className="space-y-4">
          {sectionTitle("Боловсролын мэдээлэл")}
          {selectField("Хамгийн өндөр боловсролын зэрэг", "educationLevel", [
            "Бага боловсрол","Суурь боловсрол","Бүрэн дунд боловсрол",
            "Техникийн болон мэргэжлийн боловсрол","Бакалавр","Магистр","Доктор","Бусад"
          ])}
          {sectionTitle("Хамгийн сүүлд төгссөн сургууль")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Сургуулийн нэр", "lastSchoolName")}
            {field("Сургуулийн хаяг (хот, улс)", "lastSchoolAddress")}
            {dateField("Элссэн огноо", "lastSchoolFrom")}
            {dateField("Төгссөн огноо", "lastSchoolTo")}
            {field("Мэргэжил / Чиглэл", "lastSchoolField")}
            {selectField("Суралцсан хэлбэр", "lastSchoolType", ["Өдрийн","Эчнээ","Орой","Онлайн"])}
          </div>
          {yesNo("Та одоо сурч байгаа уу?", "currentlyStudying")}
          {F("currentlyStudying") === "Тийм" && <>
            {field("Одоогийн сургуулийн нэр", "currentSchoolName")}
            {field("Одоогийн сургуулийн хаяг", "currentSchoolAddress")}
          </>}
        </div>
      );

      // ── Step 13: Last 10 years activities ────────────────────────────────
      case 13: return (
        <div className="space-y-4">
          {sectionTitle("Сүүлийн 10 жилийн үйл ажиллагаа")}
          {infoBox("Сүүлийн 10 жилийн ажил, сургуулийн мэдээллийг он дарааллаар оруулна уу. Завсарлага байсан бол тайлбарлана уу.")}
          {activities.map((a, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm text-gray-700">{i + 1}-р үйл ажиллагаа</span>
                <Button size="sm" variant="ghost" className="text-red-500 h-7"
                  onClick={() => setActivities(prev => prev.filter((_, idx) => idx !== i))}>
                  <X className="w-3 h-3 mr-1" /> Устгах
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Эхлэх огноо</Label>
                  <Input type="date" value={a.fromDate} onChange={e => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, fromDate: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Дуусах огноо (одоо хүртэл бол хоосон)</Label>
                  <Input type="date" value={a.toDate} onChange={e => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, toDate: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Үйл ажиллагааны төрөл</Label>
                  <Select value={a.type} onValueChange={v => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, type: v } : x))}>
                    <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                    <SelectContent>
                      {["Цалинтай ажил","Өөрийн бизнес","Сурлага","Гэрийн эзэгтэй/эзэн","Ажилгүй","Тэтгэвэрт гарсан","Бусад"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Ажил олгогч / Сургуулийн нэр</Label>
                  <Input value={a.employer} onChange={e => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, employer: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Албан тушаал / Мэргэжил</Label>
                  <Input value={a.occupation} onChange={e => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, occupation: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Хот</Label>
                  <Input value={a.city} onChange={e => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, city: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Улс</Label>
                  <Select value={a.country} onValueChange={v => setActivities(prev => prev.map((x, idx) => idx === i ? { ...x, country: v } : x))}>
                    <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                    <SelectContent className="max-h-60">{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full border-dashed" onClick={() => setActivities(prev => [...prev, { fromDate: "", toDate: "", type: "", employer: "", occupation: "", city: "", country: "" }])}>
            + Үйл ажиллагаа нэмэх
          </Button>
        </div>
      );

      // ── Step 14: Canada travel plan ───────────────────────────────────────
      case 14: return (
        <div className="space-y-4">
          {sectionTitle("Канад руу явах төлөвлөгөө")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dateField("Канадад ирэх төлөвлөсөн огноо", "plannedArrival")}
            {dateField("Канадаас гарах төлөвлөсөн огноо", "plannedDeparture")}
            {field("Канадад хэр удаан байх вэ?", "plannedDuration", "Жишээ: 2 долоо хоног")}
          </div>
          {field("Канадад байх хаяг (зочид буудал, найзын гэр гэх мэт)", "canadaAddress")}
          {field("Канадад байх хотууд", "canadaCities", "Жишээ: Toronto, Vancouver")}
          {textareaField("Аяллын зорилго дэлгэрэнгүй", "tripPurposeDetail")}
          {yesNo("Та Канадад хэн нэгэнтэй уулзах уу?", "meetingInCanada")}
          {F("meetingInCanada") === "Тийм" && <>
            {field("Уулзах хүний нэр", "meetingPersonName")}
            {field("Уулзах хүний утас / имэйл", "meetingPersonContact")}
            {field("Уулзах хүний Канад дахь хаяг", "meetingPersonAddress")}
          </>}
        </div>
      );

      // ── Step 15: Inviting person ──────────────────────────────────────────
      case 15: return (
        <div className="space-y-4">
          {sectionTitle("Урьж байгаа хүн / Байгууллага")}
          {yesNo("Танийг Канадад урьж байгаа хүн эсвэл байгууллага байгаа юу?", "hasInviter")}
          {F("hasInviter") === "Тийм" && <>
            {selectField("Урьж байгаа этгээд", "inviterType", [
              "Хувь хүн","Байгууллага / Компани","Их сургууль / Сургуулийн байгууллага","Бусад"
            ])}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Урьж байгаа хүн/байгууллагын нэр", "inviterName")}
              {field("Утас", "inviterPhone")}
              {field("Имэйл", "inviterEmail")}
              {field("Хаяг (дэлгэрэнгүй)", "inviterAddress")}
              {selectField("Таны хамаарал", "inviterRelationship", [
                "Гэр бүл","Хамаатан","Найз","Бизнесийн түнш","Ажил олгогч","Сургуулийн байгууллага","Бусад"
              ])}
            </div>
            {yesNo("Урьж байгаа хүн нь Канадын иргэн эсвэл байнгын оршин суугч уу?", "inviterCanadianStatus")}
          </>}
        </div>
      );

      // ── Step 16: Travel history ───────────────────────────────────────────
      case 16: return (
        <div className="space-y-4">
          {sectionTitle("Аяллын түүх (сүүлийн 10 жил)")}
          {infoBox("Сүүлийн 10 жилд зочилсон улс орнуудаа оруулна уу. Монголоос гадна аль улсад байсан бэ?")}
          {travels.map((t, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm text-gray-700">{i + 1}-р аялал</span>
                <Button size="sm" variant="ghost" className="text-red-500 h-7"
                  onClick={() => setTravels(prev => prev.filter((_, idx) => idx !== i))}>
                  <X className="w-3 h-3 mr-1" /> Устгах
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Улс</Label>
                  <Select value={t.country} onValueChange={v => setTravels(prev => prev.map((x, idx) => idx === i ? { ...x, country: v } : x))}>
                    <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                    <SelectContent className="max-h-60">{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Зорилго</Label>
                  <Select value={t.purpose} onValueChange={v => setTravels(prev => prev.map((x, idx) => idx === i ? { ...x, purpose: v } : x))}>
                    <SelectTrigger className="bg-white h-8 text-sm"><SelectValue placeholder="Сонгоно уу" /></SelectTrigger>
                    <SelectContent>
                      {["Аялал","Бизнес","Сурлага","Ажил","Гэр бүл/Хамаатан","Бусад"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Ирсэн огноо</Label>
                  <Input type="date" value={t.fromDate} onChange={e => setTravels(prev => prev.map((x, idx) => idx === i ? { ...x, fromDate: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Явсан огноо</Label>
                  <Input type="date" value={t.toDate} onChange={e => setTravels(prev => prev.map((x, idx) => idx === i ? { ...x, toDate: e.target.value } : x))} className="bg-white h-8 text-sm" />
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full border-dashed" onClick={() => setTravels(prev => [...prev, { country: "", fromDate: "", toDate: "", purpose: "" }])}>
            + Аялал нэмэх
          </Button>
          {travels.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Аялаагүй бол хоосон орхиж болно</p>}
        </div>
      );

      // ── Step 17: Visa refusals ────────────────────────────────────────────
      case 17: return (
        <div className="space-y-4">
          {sectionTitle("Визний татгалзал")}
          {yesNo("Та ямар нэгэн улсаас виз татгалзуулж байсан уу?", "visaRefused")}
          {F("visaRefused") === "Тийм" && <>
            {infoBox("Аль улс, хэдэн онд, ямар шалтгаанаар татгалзсаныг дэлгэрэнгүй бичнэ үү.")}
            {textareaField("Визний татгалзлын дэлгэрэнгүй мэдээлэл", "visaRefusalDetail", 4)}
          </>}
          {yesNo("Та ямар нэгэн улсаас хөөгдсөн эсвэл депортацид орсон уу?", "deported")}
          {F("deported") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "deportedDetail", 3)}
          {yesNo("Та ямар нэгэн улсад виз эсвэл оршин суух зөвшөөрлийн хугацаа хэтрүүлж байсан уу?", "overstayed")}
          {F("overstayed") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "overstayedDetail", 3)}
        </div>
      );

      // ── Step 18: Health ───────────────────────────────────────────────────
      case 18: return (
        <div className="space-y-4">
          {sectionTitle("Эрүүл мэндийн мэдээлэл")}
          {yesNo("Та сүрьеэ (tuberculosis) өвчтэй байсан уу эсвэл өвчтэй хүнтэй ойр харьцаж байсан уу?", "tbHistory")}
          {F("tbHistory") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "tbDetail")}
          {yesNo("Та сүүлийн 5 жилд эрүүл мэндийн шалтгаанаар нэг жил буюу түүнээс дээш хугацаагаар эмнэлэгт хэвтэж байсан уу?", "longHospitalization")}
          {F("longHospitalization") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "longHospitalizationDetail")}
          {yesNo("Та Канадын нийгмийн халамжид хамрагдах шаардлагатай болж болох эрүүл мэндийн байдалтай юу?", "requiresHealthCare")}
          {F("requiresHealthCare") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "requiresHealthCareDetail")}
        </div>
      );

      // ── Step 19: Criminal / Military ─────────────────────────────────────
      case 19: return (
        <div className="space-y-4">
          {sectionTitle("Гэмт хэрэг / Цэргийн алба")}
          {yesNo("Та ямар нэгэн улсад гэмт хэрэгт холбогдож, шүүгдэж байсан уу?", "criminalRecord")}
          {F("criminalRecord") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар (улс, он, хэргийн төрөл, шийтгэл)", "criminalDetail", 4)}
          {yesNo("Та ямар нэгэн улсын цэргийн албанд үйлчилж байсан уу?", "militaryService")}
          {F("militaryService") === "Тийм" && <>
            {countryField("Цэргийн алба хааж байсан улс", "militaryCountry")}
            {field("Цэргийн салбар / Нэгж", "militaryBranch")}
            {field("Цэргийн зэрэг / Дугаар", "militaryRank")}
            {dateField("Цэргийн алба эхэлсэн огноо", "militaryFrom")}
            {dateField("Цэргийн алба дуусгасан огноо", "militaryTo")}
          </>}
          {yesNo("Та хүний эрхийн зөрчил, дайны гэмт хэрэгт холбогдсон байгууллагад гишүүн байсан уу?", "humanRightsViolation")}
          {F("humanRightsViolation") === "Тийм" && textareaField("Дэлгэрэнгүй тайлбар", "humanRightsDetail")}
        </div>
      );

      // ── Step 20: Financial info ───────────────────────────────────────────
      case 20: return (
        <div className="space-y-4">
          {sectionTitle("Санхүүгийн мэдээлэл")}
          {selectField("Аяллын зардлыг хэн санхүүжүүлэх вэ?", "fundingSource", [
            "Өөрөө","Ажил олгогч","Эцэг эх","Эхнэр/нөхөр","Хамаатан","Канад дахь урьж байгаа хүн","Бусад"
          ])}
          {field("Банкны дансны ойролцоо үлдэгдэл (USD)", "bankBalance")}
          {field("Сарын цалин / орлого (USD)", "monthlyIncome")}
          {yesNo("Та үл хөдлөх хөрөнгөтэй юу?", "hasRealEstate")}
          {F("hasRealEstate") === "Тийм" && field("Үл хөдлөх хөрөнгийн тайлбар", "realEstateDetail")}
          {yesNo("Та хадгаламжтай юу?", "hasSavings")}
          {F("hasSavings") === "Тийм" && field("Хадгаламжийн ойролцоо хэмжээ (USD)", "savingsAmount")}
          {textareaField("Аяллын нийт төсөвлөсөн зардал (USD)", "tripBudget")}
        </div>
      );

      // ── Step 21: Ties to home country ─────────────────────────────────────
      case 21: return (
        <div className="space-y-4">
          {sectionTitle("Эх орондоо буцах холбоо")}
          {infoBox("Канадын виз шийдвэрлэхэд таны эх орондоо буцах холбоо маш чухал. Дараах мэдээллийг дэлгэрэнгүй бичнэ үү.")}
          {yesNo("Та Монголд ажлын байртай юу?", "hasJobInMongolia")}
          {F("hasJobInMongolia") === "Тийм" && <>
            {field("Ажлын байрны нэр / Байгууллага", "jobInMongolia")}
            {field("Ажлын байрны хаяг", "jobAddressInMongolia")}
          </>}
          {yesNo("Та Монголд бизнестэй юу?", "hasBusinessInMongolia")}
          {F("hasBusinessInMongolia") === "Тийм" && field("Бизнесийн тайлбар", "businessInMongolia")}
          {yesNo("Та Монголд үл хөдлөх хөрөнгөтэй юу?", "hasPropertyInMongolia")}
          {yesNo("Та Монголд гэр бүл, хамаатан байдаг уу?", "hasFamilyInMongolia")}
          {textareaField("Монголд буцах бусад шалтгаан, холбоо (дэлгэрэнгүй)", "otherTiesToMongolia", 4)}
        </div>
      );

      // ── Step 22: Documents ────────────────────────────────────────────────
      case 22: return (
        <div className="space-y-5">
          {sectionTitle("Баримт бичиг оруулах")}
          {infoBox("PDF, JPG, PNG хэлбэрт, 15MB хүртэл. Мэдэхгүй эсвэл байхгүй баримт бичгийг алгасаж болно.")}
          {DOC_CATEGORIES.map(cat => (
            <div key={cat.key} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">{cat.label}</p>
              <div className="flex flex-wrap gap-2">
                {(docs[cat.key] ?? []).map((doc, i) => (
                  <div key={i} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs text-blue-700">
                    {doc.uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                    <span className="max-w-[120px] truncate">{doc.fileName}</span>
                    {!doc.uploading && (
                      <button onClick={() => handleDeleteDoc(cat.key, doc)} className="ml-1 text-red-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-blue-700 hover:text-blue-900">
                <Upload className="w-4 h-4" />
                <span>Файл нэмэх</span>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(cat.key, f); e.target.value = ""; }} />
              </label>
            </div>
          ))}
        </div>
      );

      // ── Step 23: GCKey preparation ────────────────────────────────────────
      case 23: return (
        <div className="space-y-4">
          {sectionTitle("GCKey бэлтгэл")}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
            <h4 className="font-semibold text-blue-900">GCKey гэж юу вэ?</h4>
            <p className="text-sm text-blue-800">
              GCKey нь Канадын засгийн газрын онлайн үйлчилгээнд нэвтрэх нэвтрэлтийн систем юм.
              Та GCKey бүртгэлтэй болсноор IRCC (Канадын иммиграцийн) порталд нэвтэрч визний өргөдлөө гаргаж болно.
            </p>
            <div className="space-y-2 text-sm text-blue-800">
              <p className="font-medium">GCKey үүсгэхэд дараах зүйлс хэрэгтэй:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Имэйл хаяг (нэг имэйл = нэг GCKey)</li>
                <li>Нэвтрэх нэр (username) — 8-16 тэмдэгт, латин үсэг + тоо</li>
                <li>Нууц үг — 8-16 тэмдэгт, том, жижиг үсэг + тоо + тусгай тэмдэгт</li>
                <li>Нууц асуулт, хариулт (5 асуулт)</li>
              </ul>
            </div>
          </div>
          {field("GCKey нэвтрэх нэр (username) — байгаа бол", "gcKeyUsername")}
          {field("GCKey-тэй холбоотой имэйл", "gcKeyEmail", "example@gmail.com", "email")}
          {textareaField("GCKey-тэй холбоотой нэмэлт мэдээлэл", "gcKeyNotes")}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Анхааруулга:</strong> GCKey нэвтрэх нэр, нууц үгийг бидэнтэй хуваалцах шаардлагагүй. Зөвхөн нэвтрэх нэрийг оруулна уу.
          </div>
        </div>
      );

      // ── Step 24: Final confirmation ───────────────────────────────────────
      case 24: return (
        <div className="space-y-5">
          {sectionTitle("Эцсийн баталгаажуулалт")}

          {/* Terms expandable card */}
          <div className="border border-blue-200 rounded-xl overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-5 py-4 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
              onClick={() => { setTermsOpen(o => !o); setTermsRead(true); }}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-700 flex-shrink-0" />
                <span className="font-semibold text-blue-800 text-sm">Үйлчилгээний нөхцөл болон хариуцлагын мэдэгдэл</span>
              </div>
              <span className="text-blue-600 text-xs font-medium ml-2">{termsOpen ? "Хаах ▲" : "Нээж унших ▼"}</span>
            </button>
            {termsOpen && (
              <div className="bg-white px-5 py-4 text-sm text-gray-700 space-y-3 border-t border-blue-100">
                <p className="font-semibold text-gray-800">1. Мэдээллийн үнэн зөв байдал</p>
                <p>Энэхүү анкетэд оруулсан бүх мэдээлэл үнэн зөв байх ёстой бөгөөд мэдээллийн үнэн зөвийг өргөдөл гаргагч өөрөө бүрэн хариуцна. Худал мэдээлэл оруулсан тохиолдолд визний өргөдөл шууд татгалзагдах бөгөөд цаашид Канад руу виз авах боломж хаагдаж болно.</p>
                <p className="font-semibold text-gray-800">2. Визний шийдвэр</p>
                <p>Визний эцсийн шийдвэрийг Канадын Иммиграци, Дүрвэгсэд ба Иргэний харьяалал (IRCC) байгууллага гаргана. Энэ шийдвэр нь "Монголиан Юнайтед Дрийм Консалтинг" ХХК-ийн хяналтаас бүрэн гадна бөгөөд бид визний үр дүнд хариуцлага хүлээхгүй.</p>
                <p className="font-semibold text-gray-800">3. Үйлчилгээний хамрах хүрээ</p>
                <p>"Монголиан Юнайтед Дрийм Консалтинг" ХХК нь таны өргөдлийн материалыг бэлтгэх, шалгах, зөвлөгөө өгөх, таны өмнөөс Канадын визний өргөдлийг хариуцан мэдүүлэх үйлчилгээ үзүүлнэ. Бид таны мэдээллийг нууцлалтай хадгалж, зөвхөн визний өргөдлийн зорилгоор ашиглана.</p>
                <p className="font-semibold text-gray-800">4. Хувийн мэдээлэл ашиглах эрх олгох</p>
                <p>Би, өргөдөл гаргагч, "Монголиан Юнайтед Дрийм Консалтинг" ХХК-д энэхүү маягтад оруулсан хувийн мэдээлэл, баримт бичгүүдийг Канадын аяллын визний өргөдлийг миний өмнөөс бэлтгэх, мэдүүлэх зорилгоор ашиглах эрхийг олгож байна. Компани миний мэдээллийг зөвхөн энэхүү зорилгоор ашиглах бөгөөд гуравдагч этгээдэд дамжуулахгүй болохыг ойлгож байна.</p>
                <p className="font-semibold text-gray-800">5. Нууцлал</p>
                <p>Таны оруулсан хувийн мэдээлэл болон баримт бичгүүдийг гуравдагч этгээдэд дамжуулахгүй. Мэдээллийг зөвхөн Канадын визний өргөдлийн зорилгоор ашиглана.</p>
                <p className="font-semibold text-gray-800">6. Хариуцлагын хязгаарлалт</p>
                <p>Мэдээлэл дутуу, буруу эсвэл хоцорсон байсны улмаас визний өргөдөл татгалзагдсан тохиолдолд "Монголиан Юнайтед Дрийм Консалтинг" ХХК хариуцлага хүлээхгүй. Өргөдлийн материалын бүрэн байдлыг өргөдөл гаргагч өөрөө хянах үүрэгтэй.</p>
              </div>
            )}
          </div>

          {/* Single agreement checkbox */}
          <div className={`border rounded-xl p-4 transition-colors ${termsRead ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-start gap-3">
              <Checkbox
                id="agreeAll"
                checked={agreements.a1}
                onCheckedChange={v => setAgreements({ a1: !!v, a2: !!v, a3: !!v, a4: !!v, a5: !!v })}
                disabled={!termsRead}
              />
              <label htmlFor="agreeAll" className={`text-sm cursor-pointer leading-relaxed ${termsRead ? 'text-gray-800' : 'text-gray-400'}`}>
                Дээрх үйлчилгээний нөхцөл болон хариуцлагын мэдэгдлийг бүрэн уншиж танилцлаа. Оруулсан мэдээлэл үнэн зөв бөгөөд нөхцлүүдийг зөвшөөрч байна.
                {!termsRead && <span className="block text-xs text-orange-500 mt-1">⚠ Эхлээд нөхцлийг нээж уншина уу</span>}
              </label>
            </div>
          </div>

          {/* Signature */}
          <div className="space-y-2">
            <Label className="font-medium">Гарын үсэг (нэрээ бичнэ үү)</Label>
            <Input
              value={signatureName}
              onChange={e => setSignatureName(e.target.value)}
              placeholder="Нэрээ латин үсгээр бичнэ үү"
              className="bg-white"
            />
            <p className="text-xs text-gray-500">Нэрээ бичснээр та дээрх бүх нөхцөлийг зөвшөөрч байна.</p>
          </div>

          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold"
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !agreements.a1 || !signatureName.trim()}
          >
            {submitMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            Маягт илгээх
          </Button>
        </div>
      );

      default: return null;
    }
  };

  // ─── Loading / Error states ───────────────────────────────────────────────
  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center max-w-sm">
        <div className="text-5xl mb-4">🔗</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Линк олдсонгүй</h2>
        <p className="text-gray-500 text-sm">Линк хүчингүй болсон эсвэл буруу байна. United Dream Mongolia-тай холбогдоно уу.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center max-w-sm">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Амжилттай илгээгдлээ!</h2>
        <p className="text-gray-600 text-sm mb-4">Таны мэдээллийг хүлээн авлаа. United Dream Mongolia-ийн баг тантай удахгүй холбогдох болно.</p>
        <p className="text-xs text-gray-400">dreamvisamgl.com</p>
      </div>
    </div>
  );

  const progress = Math.round((currentStep / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white py-4 px-4 sticky top-0 z-10 shadow-md">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-bold text-base">🍁 Канадын аяллын виз</h1>
            <p className="text-red-200 text-xs">United Dream Mongolia</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {saving && <><Loader2 className="w-3 h-3 animate-spin" /><span className="text-red-200 text-xs">Хадгалж байна...</span></>}
            <span className="text-red-200 text-xs">{currentStep + 1}/{STEPS.length}</span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-2xl mx-auto mt-2">
          <div className="bg-red-800 rounded-full h-1.5">
            <div className="bg-white rounded-full h-1.5 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
          {STEPS.map((step, i) => (
            <button
              key={i}
              onClick={() => i < currentStep && setCurrentStep(i)}
              className={`shrink-0 w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                i === currentStep ? "bg-red-700 text-white" :
                i < currentStep ? "bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer" :
                "bg-gray-100 text-gray-400 cursor-default"
              }`}
              title={step}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-sm font-semibold text-gray-700 mt-1">{STEPS[currentStep]}</p>
      </div>

      {/* Form content */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 min-h-[400px]">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-4 pb-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Өмнөх
          </Button>

          {currentStep < STEPS.length - 1 && (
            <Button
              onClick={handleNext}
              disabled={saving}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Дараах <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
